import { FC, memo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

interface IErrorStateProps {
  message?: string;
  onRetry?: () => void;
  retryLabel?: string;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  text-align: center;
`;

const IconContainer = styled.div`
  width: 80px;
  height: 80px;
  background: #fef3f2;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
`;

const ErrorIcon = styled.div`
  width: 40px;
  height: 40px;
  color: #f04438;
  
  svg {
    width: 100%;
    height: 100%;
  }
`;

const Title = styled.h3`
  font-weight: 600;
  font-size: 18px;
  color: #130080;
  margin: 0 0 8px;
`;

const Message = styled.p`
  font-size: 14px;
  color: #697586;
  margin: 0 0 24px;
  max-width: 400px;
`;

const RetryButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: #5b3fff;
  color: white;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: #4a2fe6;
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const AlertCircleIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
    <line x1="12" y1="8" x2="12" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <circle cx="12" cy="16" r="1" fill="currentColor" />
  </svg>
);

const RefreshIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M1 4V10H7"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M23 20V14H17"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10M23 14L18.36 18.36A9 9 0 0 1 3.51 15"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ErrorState: FC<IErrorStateProps> = memo(({ message, onRetry, retryLabel }) => {
  const { t } = useTranslation();

  return (
    <Container>
      <IconContainer>
        <ErrorIcon>
          <AlertCircleIcon />
        </ErrorIcon>
      </IconContainer>
      <Title>{t('COMMON.ERROR_OCCURRED') || 'Something went wrong'}</Title>
      <Message>
        {message || t('CHANNELS.ERROR_LOADING') || 'Failed to load channels. Please try again.'}
      </Message>
      {onRetry && (
        <RetryButton onClick={onRetry}>
          <RefreshIcon />
          {retryLabel || t('COMMON.TRY_AGAIN') || 'Try Again'}
        </RetryButton>
      )}
    </Container>
  );
});

ErrorState.displayName = 'ErrorState';

export default ErrorState;
